import {
    KEY_TO_JOIN_BASE_AND_SUPPORTING_DATA,
    DataSourceConfiguration,
    modulePayloadMapping,
    SKIP_REQ_VALIDATION_ROUTES,
    productTrendAnalysisChartOrder,
    deepDiveMetricsConfig,
    CategoryInsightsOverviewSections,
    keysToMergeVisibilityModuleSectionData,
    productDeepDiveModuleSections,
    keysToMergeCategoryOverviewModuleSectionData,
    FREQUENCY_SLICER_OPTIONS,
    channelTypeMapping,
    TaggingActionTypes,
    COMPONENT_FILTER_QUERY_CONFIG,
} from "@ts/constants";
import {
    getRequestParams,
    getClientId,
    populateRules,
    getActiveModule,
    decodeRefreshTokenMetaData,
    encryptData,
    getQueryParams,
    getProductPageLink,
} from "@ts/utils";
import {
    formatDataBasedOnFrequency,
    formatRatingAndReviewsData,
    formatPricingData,
} from "./widget";
import { formatVisibilityData, formatAvailabilityData } from "./widget";
import {
    formatCategoryOverviewData,
    formatCategoryAvailabilityData,
} from "./widget";
import { formatCategoryAvailabilityDataEcomm } from "./widget";
import { showToast } from "./modals";
import { mapBaseDataWithSupportingData } from "@workers/merge.worker";
import { request } from "@workers/request.worker";
import { createWorkers, getCountryCode } from "@ts/utils";
import { getDateObj, getSelectedDateRange } from "./date-range";
import store from "@/store";
import * as Sentry from "@sentry/react";
import {
    IConfigs,
    TBaseAndSupport,
    TBaseAndSupportDecompressed,
    TCommonRowObj,
    TStringObj,
    TRequestedParams,
    TRetrieveData,
    TRetrieveResponse,
    TTimezone,
    TReports,
    TAdminSettings,
    IUserCountryDetails,
    IFixedReviewOptions,
    IDeepDiveModalConfig,
    IFunnelStructuredData,
    IDeepdiveOverviewFetchParams,
    TCommonRowData,
    Campaign,
    IReviewsSkuData,
    ProductTrendData,
    OptionValues,
    ProductDeepdiveModules,
    ICreateClientPayload,
    IUpdateClientPayload,
    IClientGroup,
    IClientSubgroup,
    IPlan,
    IClientIntegration,
    IClientResponse,
    IModuleData,
    CommonWidgetMetrics,
    AggregationField,
    ProductDeepdiveOverviewSections,
    TPlanTrackingConfig,
    IClientJob,
    IClientListItem,
    ICreateBrandPayload,
    TResponseWithStatusCode,
    IChannels,
    IReportsData,
    IAddReportAccessData,
    ICategoryAdvFilters,
    SubModule,
    TPaModuleReducerActions,
    ProductAndCategoryAnalyticsModuleType,
    RevstackCubeIds,
    WidgetType,
    Channels,
    Keyword,
    ProductAd,
    ITagTypeAggregate,
    AdGroup,
    Alert,
    IAdvancedFiltersParams,
    IUserRecord
} from "./common-types";
import { Store } from "antd/es/form/interface";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { updateUserCountryDetails } from "@features/adminSettingsSlice";
import { filter, map, mapObject } from "underscore";
import { updateRequestMapping } from "@features/requestMappingSlice";
import { doLogout } from "@services/UserService";
import { updateRefreshTokenMetaData } from "@features/tokenDataSlice";
import { renderFunnelReport, TDate, updateChart } from "./funnel";
import { SetStateAction } from "react";
import { setCampaignsData } from "@features/gridDataSlice";
import {
    getStructuredData,
    getCompiledRoasSpendDataFromFunnel,
    getCompiledRoasSpendDataFromKeywords,
    getCompiledProductTrendsData,
} from "./widget";
import { updateErrorStatusCode } from "@/features/errorStatusCodeSlice";
import { updateIsExcelUpload } from "@features/isExcelUploadSlice";
import {
    removeRevstackDashboardWidgetDataKey,
    updateRevstackDashboardWidgetData,
} from "@/features/revstackWidgetsSlice";
import {
    generateCubeFilters,
    buildQueriesFromWidgetsByCubeId,
} from "./revstack/helpers";
import { updateChannel } from "@/features/channelSlice";

const generateHashValue = async (data: string) => {
    try {
        const encoder = new TextEncoder();
        const encodedData = encoder.encode(data);

        const hashBuffer = await crypto.subtle.digest("SHA-256", encodedData);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
            .map((byte) => byte.toString(16).padStart(2, "0"))
            .join("");
        return hashHex;
    } catch (error) {
        console.error("Error hashing data:", error);
    }
};

const requestMappingValidation = (
    activeModule: string,
    requestMappingObj: Record<any, any>,
    requestMapping: Record<any, any>
) => {
    if (!requestMapping[activeModule]) {
        store.dispatch(
            updateRequestMapping({
                ...requestMapping,
                [activeModule]: [requestMappingObj],
            })
        );
        return;
    }

    const existingRequests = [...requestMapping[activeModule]];

    for (const existingRequest of existingRequests) {
        if (existingRequest.requestHash === requestMappingObj.requestHash) {
            if (
                new Date(existingRequest.expiryTime).getTime() +
                    5 * 60 * 1000 <=
                new Date(requestMappingObj.expiryTime).getTime()
            ) {
                existingRequest.expiryTime =
                    new Date().getTime() + 5 * 60 * 1000;
                store.dispatch(
                    updateRequestMapping({
                        ...requestMapping,
                        [activeModule]: existingRequests,
                    })
                );
            } else {
                showToast(
                    "danger",
                    "Your request has been previously submitted"
                );
                return;
            }
        } else {
            existingRequest.requestHash = requestMappingObj.requestHash;
            existingRequest.expiryTime = new Date().getTime() + 5 * 60 * 1000;
            store.dispatch(
                updateRequestMapping({
                    ...requestMapping,
                    [activeModule]: existingRequests,
                })
            );
        }
        return;
    }

    store.dispatch(
        updateRequestMapping({
            ...requestMapping,
            [activeModule]: [...existingRequests, requestMappingObj],
        })
    );
};

export const getClientLogoUrl = async (
    clientId: number
): Promise<string | null> => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}/logo`,
            method: "GET",
        });

        if (!response.status) {
            return null;
        }

        return response.data.logo_url;
    } catch (err) {
        console.error(`Error fetching logo for client ${clientId}:`, err);
        return null;
    }
};

export const sendRequest = async (
    config: IConfigs,
    isSpecificClientRequest = true,
    isWorkerNeeded = true,
    isModuleRequired = true,
    requestType = "application/json",
    streamingOptions?: {
        isStreaming: boolean;
        onMessage: (data: any) => void;
        onClose?: () => void;
        onError?: (err: any) => void;
        signal?: AbortSignal;
    },
    additionalHeaders: Record<string, string> = {}
) => {
    const { channel, isExcelUpload, requestMapping, revstackChannel } =
        store.getState() as Store;

    const clientTimezone: string = store.getState().clientTimezone.value;

    if (isModuleRequired) {
        const { activeModule } = getActiveModule();
        const payloadObjKey = (modulePayloadMapping[activeModule] || []).find(
            (key) => {
                // eslint-disable-next-line no-prototype-builtins
                return config?.data?.hasOwnProperty(key);
            }
        );

        if (payloadObjKey) {
            const payloadHash = await generateHashValue(
                JSON.stringify(config.data[payloadObjKey]) + config.type
            );

            const requestMappingObj = {
                requestHash: payloadHash,
                expiryTime: new Date().getTime() + 5 * 60 * 1000,
            };

            const reqValidationRegex = new RegExp(
                `\\b(${SKIP_REQ_VALIDATION_ROUTES.join("|")})\\b`
            );
            if (!reqValidationRegex.test(config.type)) {
                requestMappingValidation(
                    activeModule,
                    requestMappingObj,
                    structuredClone(requestMapping.value)
                );
            }
        }
    }
    const requiredConfig: IConfigs = {
        ...config,
        // Since, this process is running in worker, we need to provide complete url for api request
        API_DOMAIN: `${
            process.env.VITE_NODE_ENV !== "local"
                ? `${window.location.protocol}//${window.location.host}`
                : ""
        }${process.env.VITE_API_DOMAIN || "/api"}`,
        ...(isSpecificClientRequest && {
            client: getClientId(),
            clientGroupId: store.getState().activeClientGroupId.value,
            clientSubGroupId: store.getState().clientSubGroupId.value,
            channel: channel.value,
            country: getCountryCode(),
            revstackChannel: revstackChannel.value,
        }),
        ...(isModuleRequired && { module: getActiveModule().activeModule }),
        ...additionalHeaders,
        requestType,
        isExcelUpload: isExcelUpload.value,
        timezone: clientTimezone,
    };

    // Streaming support
    if (streamingOptions?.isStreaming && !isWorkerNeeded) {
        return await request(requiredConfig, streamingOptions);
    }

    const result: Partial<TResponseWithStatusCode> | undefined =
        isWorkerNeeded && window.Worker && !isExcelUpload.value
            ? await createWorkers("request", requiredConfig)
            : await request(requiredConfig);

    if (!result) {
        throw new Error("Request failed: No response received");
    }

    const { response, statusCode } = result;
    const decodedToken = decodeRefreshTokenMetaData();

    if (statusCode >= 500 && statusCode < 600) {
        Sentry.captureException(
            new Error(response?.message || "Internal Server Error")
        );
        store.dispatch(updateErrorStatusCode(true));
    }

    store.dispatch(updateRefreshTokenMetaData(decodedToken));

    // This is to logout the user if session is expired, handled through auth middleware in services
    if (
        response &&
        Object.prototype.hasOwnProperty.call(response, "session") &&
        response.session === "expired"
    ) {
        doLogout();
    }
    return response;
};

const mergeData = async (
    baseData: TBaseAndSupportDecompressed | TBaseAndSupport,
    supportingData: TBaseAndSupportDecompressed | TBaseAndSupport,
    type: string
) => {
    let response;
    if (window.Worker) {
        response = await createWorkers("merge", {
            baseData,
            supportingData,
            type,
        });
    } else {
        response = await mapBaseDataWithSupportingData({
            baseData,
            supportingData,
        });
    }

    return response;
};

const retrieveData = async ({
    rest,
    type,
    hasSupportingRequest,
    method,
}: TRetrieveData): Promise<Array<TRetrieveResponse>> => {
    const response = Promise.all([
        sendRequest({
            data: rest,
            type,
            method: method || "post",
        }),
        hasSupportingRequest
            ? sendRequest({
                  method: "get",
                  type: type.replace("/list", ""),
              })
            : false,
    ]);

    return response;
};

type TRetrieveAndMergeData = {
    rest: Record<string, string | number | boolean | TCommonRowObj>;
    type: string;
    hasSupportingRequest: boolean;
    method: string;
};

const retrieveAndMergeData = async <T = any>({
    rest,
    type,
    hasSupportingRequest,
    method,
}: TRetrieveAndMergeData): Promise<T[]> => {
    try {
        const [baseData, supportingData] = await retrieveData({
            rest,
            type,
            hasSupportingRequest,
            method,
        });

        if (!baseData.status) {
            throw new Error(`${type}: Internal Server Error`);
        }

        const response =
            hasSupportingRequest && supportingData.status
                ? await mergeData(
                      decompressData(
                          baseData.rows,
                          KEY_TO_JOIN_BASE_AND_SUPPORTING_DATA[
                              type.replace("/list", "")
                          ] as string
                      ),
                      decompressData(
                          supportingData.rows,
                          KEY_TO_JOIN_BASE_AND_SUPPORTING_DATA[
                              type.replace("/list", "")
                          ] as string
                      ),
                      type.replace("/list", "")
                  )
                : decompressData(baseData.rows);

        return response as T[];
    } catch (err: any) {
        console.error(err.message);
        Sentry.captureException(err.message);
        showToast("danger", err.message);
        return [];
    }
};

export const decompressData = (
    compressedData: TBaseAndSupport,
    objectKey?: string
): TBaseAndSupportDecompressed | TBaseAndSupport => {
    const decompressedObj = [];
    const keys = compressedData.shift();
    for (const item of compressedData) {
        const obj = {};
        for (let index = 0; index < keys.length; index++) {
            (obj as TStringObj)[keys[index]] = item[index];
        }
        decompressedObj.push(obj);
    }

    let obj = {};
    if (objectKey && objectKey.length) {
        for (const item of decompressedObj) {
            (obj as TStringObj)[
                map(objectKey, (key) => (item as TStringObj)[key]).join("##")
            ] = item;
        }
    } else {
        obj = decompressedObj;
    }
    return obj;
};

type TRestObj = Record<string, string | number | boolean | TCommonRowObj>;

type TConfigParams = {
    client?: number;
    rest?: TRestObj;
};

interface IServerConfig extends IConfigs {
    hasSupportingRequest: boolean;
    params: TConfigParams;
    displayCount: boolean;
}

const retrieveDataFromServer = async <T = any>(
    config: IServerConfig
): Promise<T[]> => {
    delete config.params["client"];
    return await retrieveAndMergeData<T>({
        rest: config.params as TRestObj,
        type: config.type,
        hasSupportingRequest: config.hasSupportingRequest,
        method: config.method,
    });
};

export const createDatasource = async <T = any>(
    type = "keywords",
    requestedParams: TRequestedParams
): Promise<T[]> => {
    const params = getRequestParams(requestedParams);
    return await retrieveDataFromServer<T>({
        params: params,
        type: DataSourceConfiguration[type].type || type,
        displayCount: DataSourceConfiguration[type].displayCount,
        hasSupportingRequest:
            DataSourceConfiguration[type].hasSupportingRequest,
        method: DataSourceConfiguration[type].method,
    });
};

export const populatePortfolios = async () => {
    return await createDatasource("portfolios", {
        dateObj: getDateObj(),
    });
};

export async function populateRulesOverview() {
    return await populateRules();
}

export const populateCampaigns = async (
    sku: string = null
): Promise<Campaign[]> => {
    return await createDatasource<Campaign>("campaigns", {
        dateObj: getDateObj(),
        ...(sku ? { sku } : {}),
    });
};

export const populateKeywords = async (): Promise<Keyword[]> => {
    return await createDatasource<Keyword>("keywords", {
        dateObj: getDateObj(),
    });
};

export const populatePlacements = async () => {
    return await createDatasource("placements", {
        dateObj: getDateObj(),
    });
};

export const populateAdGroups = async (): Promise<AdGroup[]> => {
    return await createDatasource<AdGroup>("adgroups", {
        dateObj: getDateObj(),
    });
};

export const populateProductAds = async (): Promise<ProductAd[]> => {
    return await createDatasource<ProductAd>("products", {
        dateObj: getDateObj(),
    });
};

export const populateAlerts = async (): Promise<Alert[]> => {
    return await createDatasource<Alert>("alerts", {
        dateObj: getDateObj(),
    });
};

export const populateSellersData = async () => {
    const response = await sendRequest({
        type: "buybox/sellers",
        method: "GET",
    });
    return response;
};

export const populateBrandsListData = async () => {
    const response = await sendRequest({
        type: "clients/brandslist",
        method: "GET",
    });
    return response;
};

export const getClientChannels = createAsyncThunk(
    "channels/fetchChannels",
    async (_, { rejectWithValue }) => {
        try {
            const response = await sendRequest({
                type: "user/channels",
                method: "GET",
            });

            if (!response || !response.rows || response.rows.length === 0) {
                alert("Channels not found, please refresh the page!");
                throw new Error("Unable to get channels data !!");
            }

            const currentChannel = store.getState().channel.value;

            if (!response.rows || response.rows.length === 0) {
                showToast(
                    "warning",
                    "No channels available for this client. Please select a different client."
                );
                return [];
            }

            if (!currentChannel) {
                store.dispatch(updateChannel(response.rows[0].channel_id));
            }

            return response.rows;
        } catch (err: any) {
            showToast("danger", err.message);
            return rejectWithValue(err.message);
        }
    }
);

export const updateDailyBudgetConfig = createAsyncThunk(
    "campaigns/daily-budget-config",
    async (_, { rejectWithValue }) => {
        try {
            const data = store.getState().dailyBudgetUpdatePayload.value;
            const response = await sendRequest({
                type: `campaigns/daily-budget-config`,
                data,
                method: "put",
            });

            if (!response.status) {
                throw new Error("Failed to update daily budget config");
            }
            return response;
        } catch (err: any) {
            rejectWithValue(err.message);
            showToast("danger", err.message);
        }
    }
);

export const updateRevstackChannels = createAsyncThunk(
    "revstackChannelsList/fetchRevstackChannels",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getRevstackChannels();
            return response;
        } catch (error) {
            return rejectWithValue(
                error instanceof Error
                    ? error.message
                    : "Failed to fetch revstack channels"
            );
        }
    }
);

export const updateAdminSettings = async (features: TAdminSettings) => {
    const response = await sendRequest({
        type: "admin/features",
        method: "PUT",
        data: { features },
    });
    return response;
};

export const updateUserSettingsConfig = async (
    timezone: TTimezone,
    selectedCountryDetails: IUserCountryDetails
) => {
    const { selected_country_code, selected_country_locale } =
        selectedCountryDetails;
    const response = await sendRequest({
        type: "admin/config",
        method: "PUT",
        data: {
            timezone,
            countryCode: selected_country_code,
            clientLocale: selected_country_locale,
        },
    });
    return response;
};

export const uploadReports = async (reports: Array<TReports>) => {
    const response = await sendRequest({
        type: "revstack/reports",
        method: "POST",
        data: { reports },
    });
    return response;
};

export const deleteReports = async (
    reports: Array<string>,
    reportsType: number,
    reportsToRearrange: Array<IReportsData>
) => {
    const response = await sendRequest({
        type: "revstack/remove-reports",
        method: "PUT",
        data: { reports, reportsType, reportsToRearrange },
    });
    return response;
};

export const updateReportType = async (
    reportType: number,
    reportId: string,
    reports: Array<IReportsData>
) => {
    const response = await sendRequest({
        type: `revstack/reports/${reportId}/type`,
        method: "PUT",
        data: { reportType, reportId, reports },
    });
    return response;
};

type TBuybox = {
    asin: string[];
    operation: string;
};

export const updateBuybox = async (data: TBuybox) => {
    const response = await sendRequest({
        type: "buybox/addasin",
        method: "POST",
        data,
    });
    return response;
};

type TPreferredSellers = {
    sellers: string[];
};

export const addPreferredSellers = async (data: TPreferredSellers) => {
    const response = await sendRequest({
        type: "buybox/addsellers",
        method: "POST",
        data,
    });
    return response;
};

export const getPreferredSellers = async () => {
    const response = await sendRequest({
        type: "buybox/preferredsellers",
        method: "GET",
    });
    return response;
};

export const updateBrandsMapping = async (data: number[]) => {
    const response = await sendRequest({
        type: "clients/brandslist",
        method: "PUT",
        data: { brandIds: data },
    });
    return response;
};

type TUpdateUserSettings = {
    selectedSellersData?: string[];
    selectedTimezone: TTimezone;
    selectedCountryDetails: IUserCountryDetails;
    selectedBrandsData: number[];
    onSuccess?: () => void;
};

export const updateUserSettings = async ({
    selectedSellersData,
    selectedTimezone,
    selectedCountryDetails,
    selectedBrandsData,
    onSuccess,
}: TUpdateUserSettings) => {
    try {
        const enableBuybox = store.getState().enableBuybox.value;
        const requests = [];

        if (selectedTimezone && selectedCountryDetails) {
            requests.push(
                updateUserSettingsConfig(
                    selectedTimezone,
                    selectedCountryDetails
                )
            );
        }

        if (enableBuybox && selectedSellersData) {
            requests.push(
                addPreferredSellers({ sellers: selectedSellersData })
            );
        }

        if (selectedBrandsData) {
            requests.push(updateBrandsMapping(selectedBrandsData));
        }

        if (requests.length === 0) {
            showToast(
                "danger",
                "Please select at least one valid configuration to save."
            );
            return;
        }

        const responses = await Promise.all(requests);

        const failedResponses = responses.filter((res) => !res.status);
        if (failedResponses.length > 0) {
            const errorMessage = failedResponses
                .map((res) => res.message)
                .join("\n");
            throw new Error(errorMessage);
        }

        if (selectedTimezone && selectedCountryDetails) {
            store.dispatch(updateUserCountryDetails(selectedCountryDetails));
        }

        showToast("success", "Settings saved successfully!");
        if (onSuccess) {
            onSuccess();
        }
    } catch (err: any) {
        console.error("Failed to update user settings:", err);
        showToast("danger", err.message);
        Sentry.captureException(err);
    }
};

export const getAccessToken = async () =>
    sendRequest({
        type: `revstack/get-token`,
        method: "get",
    });

export const getReport = async () =>
    sendRequest({
        type: "revstack/reports",
        method: "get",
    });

export const registerUser = async () =>
    sendRequest(
        {
            type: "user/profile",
            method: "put",
        },
        false,
        true,
        false
    );

export const getClientReports = createAsyncThunk(
    "revstack/reports",
    async (_, { rejectWithValue }) => {
        try {
            const response = await sendRequest({
                type: "revstack/reports",
                method: "GET",
            });
            if (!response) {
                throw new Error("Unable to get channels data !!");
            }
            return response.rows;
        } catch (err: any) {
            rejectWithValue(err.message);
            showToast("danger", err.message);
        }
    }
);

export const getReportTypes = createAsyncThunk(
    "revstack/reports/types",
    async (_, { rejectWithValue }) => {
        try {
            const response = await sendRequest({
                type: "revstack/reports/types",
                method: "GET",
            });
            if (!response) {
                throw new Error("Unable to get report types data !!");
            }
            return response.rows;
        } catch (err: any) {
            rejectWithValue(err.message);
            showToast("danger", err.message);
        }
    }
);

export const addEntityTags = async (
    recordIds: { [key: string]: string | number }[] | (string | number)[],
    updateMethod: string,
    updatePath: string,
    updateKey: string,
    tags: number[],
    isBulkRequest = false,
    action:
        | TaggingActionTypes.INSERT
        | TaggingActionTypes.UPDATE = TaggingActionTypes.INSERT
) => {
    const response = await sendRequest({
        type: updatePath,
        method: updateMethod,
        data: {
            [updateKey]: recordIds,
            tags,
            isBulkRequest,
            action,
        },
    });
    if (!response.status) {
        showToast("danger", response.message);
        Sentry.captureException(response.message);
    } else {
        showToast("success", response.message);
    }
    return response;
};

export const getIntegrationsAppId = async () => {
    const response = await sendRequest({
        type: "integrations/amazon",
        method: "GET",
    });
    return response;
};

export const getPortalUrl = async (
    channel: number,
    country: string,
    type: string
) => {
    const portalUrlParams = new URLSearchParams();
    portalUrlParams.append("channel", channel.toString());
    portalUrlParams.append("country", country);
    portalUrlParams.append("type", type);
    const response = await sendRequest({
        type: `integrations/amazon/portal?${portalUrlParams.toString()}`,
        method: "GET",
    });
    return response.rows;
};

export const addPortalsState = async (states: string[]) => {
    const response = await sendRequest({
        type: "integrations/state",
        method: "POST",
        data: { states },
    });
    return response;
};

export const getDeepDiveData = async (config: {
    module: string;
    section?: string;
    start: string;
    end: string;
    sku: string;
}) => {
    const { module, section, start, end, sku } = config;

    try {
        const params = new URLSearchParams({
            module,
            section,
            start,
            end,
            sku,
        });
        const response = await sendRequest({
            type: `analytics/products/deep-dive?${params.toString()}`,
            method: "get",
        });

        if (!response) throw new Error(response.message);

        return response.rows[0] || null;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

export const getCategoryInsightsData = async (config: {
    module: string;
    section?: string;
    previousStart: string;
    previousEnd: string;
    start: string;
    end: string;
    selectedCategory: string[];
    selectedSubCategory: string[];
    selectedBrand: string[];
    caseType: number;
    selectedCatAdditionalFilters: ICategoryAdvFilters;
}) => {
    const {
        module,
        section,
        start,
        end,
        previousStart,
        previousEnd,
        selectedCategory,
        selectedSubCategory,
        selectedBrand,
        caseType,
        selectedCatAdditionalFilters,
    } = config;

    try {
        const params = new URLSearchParams({
            module,
            section,
            previousStart,
            previousEnd,
            start,
            end,
            category: selectedCategory.join(","),
            subCategory: selectedSubCategory.join(","),
            brand: selectedBrand.join(","),
            caseType: caseType.toString(),
            advanceFilters: JSON.stringify(selectedCatAdditionalFilters),
        });
        const response = await sendRequest({
            type: `analytics/category-insights?${params.toString()}`,
            method: "get",
        });

        if (!response) throw new Error(response.message);

        return response.rows[0] || null;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

export const getReviewsFiltersData = async (type: "skus" | "categories") => {
    try {
        const response = await sendRequest({
            type: `reviews/filters/${type}`,
            method: "get",
        });

        if (!response) throw new Error(response.message);

        return response;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

export const getReviewsData = async (config: {
    type: "skus" | "brands";
    start: string;
    end: string;
    sku?: string;
    category?: string;
    subcategory?: string;
}) => {
    const { type, start, end, sku, category, subcategory } = config;

    try {
        const params = new URLSearchParams({ start, end });

        if (sku) params.append("sku", sku);
        if (category) params.append("category", category);
        if (subcategory) params.append("subcategory", subcategory);

        const response = await sendRequest({
            type: `reviews/${type}?${params.toString()}`,
            method: "get",
        });

        if (!response) throw new Error(response.message);

        return response?.data[0] || null;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

export const populateReviewProductsData = async (asin: string) => {
    try {
        const data = await createDatasource("product-analytics-keywords", {
            asin,
            dateObj: getDateObj(),
        });
        return data;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

export const getAllUsers = async ({
    page,
    pageSize,
    searchQuery,
}: {
    page: number;
    pageSize: number;
    searchQuery?: string;
}) => {
    try {
        const response = await sendRequest({
            type: "user",
            method: "GET",
            data: {
                page,
                pageSize,
                searchQuery,
            },
        });

        if (response && response.rows && response.rows.usersData) {
            return response.rows;
        } else {
            throw new Error("Invalid response structure from API");
        }
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getUserById = async (userId: string) => {
    try {
        const response = await sendRequest({
            type: `user/${userId}`,
            method: "GET",
        });

        if (response && response.status && response.user) {
            return response.user;
        } else {
            throw new Error(
                response.message || "Invalid response structure from API"
            );
        }
    } catch (err) {
        console.error("Error in getUserById:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getRolePermissions = async () => {
    try {
        const response = await sendRequest({
            type: "permissions",
            method: "GET",
        });

        return response.rows;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

export const getUserRoles = async () => {
    try {
        const response = await sendRequest({
            type: "roles",
            method: "GET",
        });

        return response.rows;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

export const updateRolePermissions = async (data: any) => {
    try {
        const response = await sendRequest({
            type: "permissions/update",
            method: "PUT",
            data,
        });

        if (response.status) {
            return {
                status: true,
                message: "Role permissions updated successfully",
            };
        } else {
            return {
                status: false,
                message: response.message || "Error updating role permissions",
            };
        }
    } catch (err) {
        console.error("Error in updateRolePermissions:", err);
        Sentry.captureException(err);
        if (err instanceof Error) {
            return {
                status: false,
                message: `An unexpected error occurred: ${err.message}`,
            };
        }
        return { status: false, message: "An unexpected error occurred" };
    }
};

export const getSavedRolePermissions = async () => {
    try {
        const response = await sendRequest({
            type: "permissions/saved",
            method: "GET",
        });

        // Assuming the response.rows contains an object with role IDs as keys and arrays of permission IDs as values
        return response.rows;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
        return {};
    }
};

export const populateTagTypeAggregatedView = async (): Promise<
    ITagTypeAggregate[]
> => {
    return (await createDatasource)<ITagTypeAggregate>("tag-type-view", {
        dateObj: getDateObj(),
    });
};

export const fetchReviewsBrandData = async (
    setReviewsBrandData: React.Dispatch<SetStateAction<any>>,
    setReviewsLoader: React.Dispatch<SetStateAction<boolean>>,
    fixedOptions: IFixedReviewOptions
) => {
    try {
        const { category, subcategory } = getQueryParams();
        const categoryParams =
            category === "all"
                ? fixedOptions.categories.reduce((acc, c) => {
                      if (c.value !== "*") {
                          acc.push(c.value);
                      }
                      return acc;
                  }, [])
                : category;

        const subCategoryParams =
            subcategory === OptionValues.ALL
                ? fixedOptions.subCategories.reduce((acc, c) => {
                      if (c.value !== "*") {
                          acc.push(c.value);
                      }
                      return acc;
                  }, [])
                : subcategory;

        setReviewsLoader(true);
        const date: TDate = getSelectedDateRange();
        const response = await getReviewsData({
            type: "brands",
            start: date.startDate.format("YYYY-MM-DD"),
            end: date.endDate.format("YYYY-MM-DD"),
            category: categoryParams,
            subcategory: subCategoryParams,
        });

        if (!response) throw new Error(response.message);
        setReviewsBrandData(response);
        setReviewsLoader(false);
    } catch (err) {
        console.error(err);
        showToast("danger", (err as Error).message);
    }
};

export const fetchReviewsSkuData = async (
    setReviewsSkuData: React.Dispatch<SetStateAction<IReviewsSkuData>>,
    setReviewsLoader: React.Dispatch<SetStateAction<boolean>>,
    selectedSKU?: string
) => {
    try {
        setReviewsLoader(true);
        const date: TDate = getSelectedDateRange();
        const response = await getReviewsData({
            type: "skus",
            start: date.startDate.format("YYYY-MM-DD"),
            end: date.endDate.format("YYYY-MM-DD"),
            sku: selectedSKU ? selectedSKU : getQueryParams().sku,
        });

        if (!response) throw new Error(response.message);

        setReviewsSkuData(response);
        setReviewsLoader(false);
    } catch (err) {
        console.error(err);
        showToast("danger", (err as Error).message);
    }
};

export const setUserDetails = async () => {
    try {
        const response = await sendRequest(
            {
                type: "user/me",
                method: "GET",
            },
            false,
            true,
            false
        );

        const userData = response?.rows?.data;
        if (!userData) {
            console.error("No user data received from server");
            return null;
        }

        Sentry.setUser({
            email: userData.email,
        });

        // Encrypt and save in localStorage
        const encryptedData = encryptData(JSON.stringify(userData));
        localStorage.setItem("userData", encryptedData);
        return userData;
    } catch (err) {
        Sentry.captureException(err);
        throw err;
    }
};

const mergeModuleData = (
    prevData: IModuleData,
    newData: IModuleData
): IModuleData => {
    return {
        titles: [...(prevData?.titles || []), ...newData.titles].sort(
            (a, b) =>
                productTrendAnalysisChartOrder.indexOf(a) -
                productTrendAnalysisChartOrder.indexOf(b)
        ),
        chartOptions: {
            ...(prevData?.chartOptions || {}),
            ...newData.chartOptions,
        },
    };
};

export const fetchDeepDiveOverviewData = async (
    setLoadingState: any,
    section: string,
    selectedSKU: string,
    date: TDate,
    setModuleData: React.Dispatch<React.SetStateAction<IDeepDiveModalConfig>>
) => {
    try {
        const data = await getDeepDiveData({
            module: ProductDeepdiveModules.OVERVIEW,
            section,
            start: date.startDate.format("YYYY-MM-DD"),
            end: date.endDate.format("YYYY-MM-DD"),
            sku: selectedSKU,
        });

        let result: any;

        if (section === ProductDeepdiveOverviewSections.OVERVIEW) {
            result = formatDataBasedOnFrequency([{ [section]: data }], section);
        } else if (section === ProductDeepdiveOverviewSections.SALES_TREND) {
            result = formatDataBasedOnFrequency([{ [section]: data }], section);
        } else if (section === ProductDeepdiveOverviewSections.FUNNEL) {
            result = formatDataBasedOnFrequency([data], section);
        }

        const structuredData = await getStructuredData(section, data);

        if (section === ProductDeepdiveModules.OVERVIEW) {
            const metricsConfig = deepDiveMetricsConfig.overview;

            const formattedData = {
                dailyData: getCompiledProductTrendsData(
                    result.rawData,
                    metricsConfig,
                    CommonWidgetMetrics.CRAWL_DATE
                ),
                weeklyData: getCompiledProductTrendsData(
                    result.weeklyData,
                    metricsConfig,
                    CommonWidgetMetrics.CRAWL_DATE
                ),
                monthlyData: getCompiledProductTrendsData(
                    result.monthlyData,
                    metricsConfig,
                    CommonWidgetMetrics.CRAWL_DATE
                ),
            };

            setModuleData((prev: IDeepDiveModalConfig) => ({
                ...prev,
                productTrendAnalysis: {
                    dailyData: mergeModuleData(
                        prev.productTrendAnalysis?.dailyData,
                        formattedData.dailyData
                    ),
                    weeklyData: mergeModuleData(
                        prev.productTrendAnalysis?.weeklyData,
                        formattedData.weeklyData
                    ),
                    monthlyData: mergeModuleData(
                        prev.productTrendAnalysis?.monthlyData,
                        formattedData.monthlyData
                    ),
                },
            }));

            setLoadingState({
                type: ProductDeepdiveModules.OVERVIEW,
                payload: false,
            });
        }
        if (section === ProductDeepdiveOverviewSections.FUNNEL) {
            const metricsConfig = deepDiveMetricsConfig.funnel;

            const formattedData = {
                dailyData: getCompiledProductTrendsData(
                    result.rawData[0].combinedResponse,
                    metricsConfig,
                    CommonWidgetMetrics.DATE
                ),
                weeklyData: getCompiledProductTrendsData(
                    result.weeklyData[0].combinedResponse,
                    metricsConfig,
                    CommonWidgetMetrics.DATE
                ),
                monthlyData: getCompiledProductTrendsData(
                    result.monthlyData[0].combinedResponse,
                    metricsConfig,
                    CommonWidgetMetrics.DATE
                ),
            };

            setModuleData((prev: IDeepDiveModalConfig) => ({
                ...prev,
                productTrendAnalysis: {
                    dailyData: mergeModuleData(
                        prev.productTrendAnalysis?.dailyData,
                        formattedData.dailyData
                    ),
                    weeklyData: mergeModuleData(
                        prev.productTrendAnalysis?.weeklyData,
                        formattedData.weeklyData
                    ),
                    monthlyData: mergeModuleData(
                        prev.productTrendAnalysis?.monthlyData,
                        formattedData.monthlyData
                    ),
                },
            }));

            getCompiledRoasSpendDataFromFunnel(
                data.comparedTotalByProgramType,
                setModuleData
            );

            sessionStorage.setItem(
                "funnel-response",
                JSON.stringify({
                    dailyData: result.rawData,
                    weeklyData: result.weeklyData,
                    monthlyData: result.monthlyData,
                })
            );
            setTimeout(() => {
                renderFunnelReport(date, result.rawData[0]);
                updateChart(AggregationField.SPEND, 0);
                updateChart(AggregationField.ROAS, 1);
            }, 500);
        }

        if (section === ProductDeepdiveOverviewSections.SPEND_ROAS_DATA) {
            setModuleData((prev) => ({
                ...prev,
                [section]: {
                    chartDataSeries: {
                        ...(prev.spendRoasData?.chartDataSeries || {}),
                        ...((structuredData as IFunnelStructuredData)
                            ?.chartDataSeries || {}),
                    },
                    delta: {
                        ...(prev.spendRoasData?.delta || {}),
                        ...((structuredData as IFunnelStructuredData)?.delta ||
                            {}),
                    },
                },
            }));
        } else if (section === ProductDeepdiveOverviewSections.SALES_TREND) {
            const metricsConfig = deepDiveMetricsConfig.salesTrend;

            const formattedData = {
                dailyData: getCompiledProductTrendsData(
                    result.rawData,
                    metricsConfig,
                    CommonWidgetMetrics.CRAWL_DATE
                ),
                weeklyData: getCompiledProductTrendsData(
                    result.weeklyData,
                    metricsConfig,
                    CommonWidgetMetrics.CRAWL_DATE
                ),
                monthlyData: getCompiledProductTrendsData(
                    result.monthlyData,
                    metricsConfig,
                    CommonWidgetMetrics.CRAWL_DATE
                ),
            };

            setModuleData((prev: IDeepDiveModalConfig) => ({
                ...prev,
                productTrendAnalysis: {
                    dailyData: mergeModuleData(
                        prev.productTrendAnalysis?.dailyData,
                        formattedData.dailyData
                    ),
                    weeklyData: mergeModuleData(
                        prev.productTrendAnalysis?.weeklyData,
                        formattedData.weeklyData
                    ),
                    monthlyData: mergeModuleData(
                        prev.productTrendAnalysis?.monthlyData,
                        formattedData.monthlyData
                    ),
                },
            }));
        } else {
            setModuleData((prev) => ({
                ...prev,
                [section]: structuredData,
            }));
        }
    } catch (error) {
        console.error(`Error fetching data for section: ${section}`, error);
    }
};

const fetchDeepDiveOverviewSectionsData = async (
    params: IDeepdiveOverviewFetchParams
) => {
    const { setLoadingState, setModuleData, selectedSKU, date } = params;
    const sections = [
        "overview",
        "salesTrend",
        "funnel",
        "spendRoasData",
        "reviews",
        "digitalShelf",
    ];

    const fetchSection = async (section: string) => {
        try {
            await fetchDeepDiveOverviewData(
                setLoadingState,
                section,
                selectedSKU,
                date,
                setModuleData
            );
        } catch (error) {
            console.error(`Error fetching section ${section}:`, error);
        }
    };

    await Promise.all(sections.map(fetchSection));
};

export const fetchDeepDiveOverviewCampaignAndReviewsData = async (
    params: IDeepdiveOverviewFetchParams
) => {
    const { selectedSKU, setCampaignsLoading, setRowData, setModuleData } =
        params;
    try {
        const campaigns = await populateCampaigns(selectedSKU);
        if (campaigns) {
            store.dispatch(setCampaignsData(campaigns as Array<Campaign>));
            setCampaignsLoading(false);
        }

        const products = await populateReviewProductsData(selectedSKU);
        if (products) {
            setRowData(products as TCommonRowData);
            const spendRoasData = getCompiledRoasSpendDataFromKeywords(
                products as ProductTrendData
            );
            setModuleData((prev) => ({
                ...prev,
                spendRoasData: {
                    chartDataSeries: {
                        ...(prev.spendRoasData?.chartDataSeries || {}),
                        ...(spendRoasData?.chartDataSeries || {}),
                    },
                    delta: {
                        ...(prev.spendRoasData?.delta || {}),
                        ...(spendRoasData?.delta || {}),
                    },
                },
            }));
        }
    } catch (error) {
        console.error("Error fetching campaigns or reviews:", error);
    }
};

export const fetchOverviewSectionsData = async (
    params: IDeepdiveOverviewFetchParams
) => {
    const { setLoadingState, setCampaignsLoading } = params;
    setLoadingState({ type: "overview", payload: true });
    setCampaignsLoading(true);
    try {
        await fetchDeepDiveOverviewSectionsData(params);
        await fetchDeepDiveOverviewCampaignAndReviewsData(params);
    } catch (error) {
        console.error("Error loading data:", error);
    }
};

/**
 * TODO:
 * 1) Format data using formatter functions
 * 2) Format data here and set it in setModuleData based on the module type
 */

export const fetchDeepDiveModulesData = async (
    module: string,
    selectedSKU: string,
    date: TDate,
    setModuleData: React.Dispatch<React.SetStateAction<any>>,
    setLoadingState: any,
    section?: string
) => {
    try {
        let formattedData: any;

        setLoadingState({ type: module, payload: true });
        const data = await getDeepDiveData({
            module,
            start: date.startDate.format("YYYY-MM-DD"),
            end: date.endDate.format("YYYY-MM-DD"),
            sku: selectedSKU,
            ...(section ? { section } : {}),
        });

        let result;

        if (
            [
                ProductDeepdiveModules.PRICING,
                ProductDeepdiveModules.RATINGS_REVIEWS,
                ProductDeepdiveModules.VISIBILITY,
                ProductDeepdiveModules.AVAILABILITY,
            ].includes(module as ProductDeepdiveModules)
        ) {
            result = formatDataBasedOnFrequency(data, module);
        }

        switch (module) {
            case ProductDeepdiveModules.OVERVIEW:
                formattedData = data;
                break;
            case ProductDeepdiveModules.VISIBILITY:
                formattedData = {
                    dailyData: formatVisibilityData(result.rawData),
                    weeklyData: formatVisibilityData(result.weeklyData),
                    monthlyData: formatVisibilityData(result.monthlyData),
                };

                setModuleData((prev: any = {}) => {
                    const mergeData = (
                        timeframe: "dailyData" | "weeklyData" | "monthlyData"
                    ) => {
                        const prevData = prev?.[timeframe] ?? {};
                        const formattedTimeframeData = formattedData[timeframe];

                        return {
                            ...(section ===
                                productDeepDiveModuleSections.KEYWORD_VISIBILITY &&
                            prevData
                                ? prevData
                                : formattedTimeframeData),
                            ...Object.fromEntries(
                                keysToMergeVisibilityModuleSectionData.map(
                                    (key) => [
                                        key,
                                        section ===
                                        productDeepDiveModuleSections.KEYWORD_VISIBILITY
                                            ? formattedTimeframeData[key]
                                            : prevData?.[key] ??
                                              formattedTimeframeData[key],
                                    ]
                                )
                            ),
                        };
                    };

                    return {
                        dailyData: mergeData("dailyData"),
                        weeklyData: mergeData("weeklyData"),
                        monthlyData: mergeData("monthlyData"),
                    };
                });

                break;
            case ProductDeepdiveModules.RATINGS_REVIEWS:
                formattedData = {
                    dailyData: formatRatingAndReviewsData(result.rawData),
                    weeklyData: formatRatingAndReviewsData(result.weeklyData),
                    monthlyData: formatRatingAndReviewsData(result.monthlyData),
                };
                break;
            case ProductDeepdiveModules.PRICING:
                formattedData = {
                    dailyData: formatPricingData(result.rawData, selectedSKU),
                    weeklyData: formatPricingData(
                        result.weeklyData,
                        selectedSKU
                    ),
                    monthlyData: formatPricingData(
                        result.monthlyData,
                        selectedSKU
                    ),
                };
                break;
            case ProductDeepdiveModules.AVAILABILITY:
                formattedData = {
                    dailyData: formatAvailabilityData(result.rawData[0], date),
                    weeklyData: formatAvailabilityData(
                        result.weeklyData[0],
                        date
                    ),
                    monthlyData: formatAvailabilityData(
                        result.monthlyData[0],
                        date
                    ),
                };
                break;
            default:
                formattedData = data;
        }

        if (module !== ProductDeepdiveModules.VISIBILITY) {
            setModuleData(formattedData);
        }
        setLoadingState({ type: module, payload: false });
    } catch (error) {
        console.error(`Error fetching data for module: ${module}`, error);
    }
};

export const getAllChannelsForClientIds = async (clientId: Array<number>) => {
    try {
        const response = await sendRequest({
            type: `clients/channels?clientId=${
                clientId.length ? clientId : []
            }`,
            method: "GET",
        });
        if (!response) {
            throw new Error("Unable to get channels data !!");
        }
        return response.rows;
    } catch (err: any) {
        showToast("danger", err.message);
    }
};

export const getAllClients = async () => {
    try {
        const response = await sendRequest({
            type: "clients",
            method: "GET",
        });
        if (!response) {
            throw new Error("Unable to get clients !!");
        }
        return response.rows;
    } catch (err: any) {
        showToast("danger", err.message);
    }
};

export const getClientsList = async ({
    page,
    pageSize,
    searchQuery,
}: {
    page: number;
    pageSize: number;
    searchQuery?: string;
}) => {
    try {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
            ...(searchQuery && { searchQuery }),
        });

        const response = await sendRequest({
            type: `clients/list?${queryParams.toString()}`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to fetch clients");
        }

        return response.rows;
    } catch (err) {
        console.error("Error fetching clients:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getClientById = async (
    clientId: number
): Promise<IClientListItem> => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to fetch client");
        }

        return response.data;
    } catch (err) {
        console.error("Error fetching client:", err);
        Sentry.captureException(err);
        throw err;
    }
};

const sendClientMutation = async <TResponse, TPayload>(
    endpoint: string,
    method: "POST" | "PUT",
    data: TPayload
): Promise<TResponse> => {
    return await sendRequest(
        {
            type: endpoint,
            method,
            data,
            requestType: "application/json",
        },
        true,
        true,
        true,
        "application/json"
    );
};

export const createClient = async (
    data: ICreateClientPayload
): Promise<IClientResponse> => {
    try {
        const response = await sendClientMutation<
            IClientResponse,
            ICreateClientPayload
        >("clients", "POST", data);

        if (!response.status) {
            throw new Error(response.message || "Failed to create client");
        }

        return response;
    } catch (err) {
        console.error("Error creating client:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const updateClient = async (
    clientId: number,
    data: IUpdateClientPayload
) => {
    try {
        const response: IClientResponse = await sendClientMutation<
            IClientResponse,
            IUpdateClientPayload
        >(`clients/${clientId}`, "PUT", data);

        if (!response.status) {
            throw new Error(response.message || "Failed to update client");
        }

        return response.data;
    } catch (err) {
        console.error("Error updating client:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const uploadClientLogo = async (clientId: number, logo: File) => {
    const response = await sendRequest(
        {
            type: `clients/${clientId}/logo`,
            method: "POST",
            data: { logo } as any,
            requestType: "multipart/form-data",
        },
        true,
        true,
        true,
        "multipart/form-data"
    );
    if (!response.status) {
        throw new Error(response.message || "Failed to upload logo");
    }
    return response.data as { client_id: number; logo_url: string };
};

export const deleteClient = async (clientId: number) => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}`,
            method: "DELETE",
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to delete client");
        }

        return response.data;
    } catch (err) {
        console.error("Error deleting client:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getClientGroups = async () => {
    try {
        const response = await sendRequest({
            type: "clients/groups",
            method: "GET",
        });

        if (!response.status) {
            throw new Error(
                response.message || "Failed to fetch client groups"
            );
        }

        return response.data as IClientGroup[];
    } catch (err) {
        console.error("Error fetching client groups:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getClientSubgroups = async (groupId: number) => {
    try {
        const response = await sendRequest({
            type: `clients/groups/${groupId}/subgroups`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(
                response.message || "Failed to fetch client subgroups"
            );
        }

        return response.data as IClientSubgroup[];
    } catch (err) {
        console.error("Error fetching client subgroups:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getAvailableChannels = async (countryCode: string) => {
    try {
        const response = await sendRequest({
            type: `clients/channels/${countryCode}`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to fetch channels");
        }

        return response.data as IChannels[];
    } catch (err) {
        console.error("Error fetching channels:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getAvailablePlans = async () => {
    try {
        const response = await sendRequest({
            type: "clients/plans",
            method: "GET",
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to fetch plans");
        }

        return response.data as IPlan[];
    } catch (err) {
        console.error("Error fetching plans:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getClientUsers = async (
    clientId: number,
    page = 1,
    pageSize = 10,
    searchQuery?: string
) => {
    try {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
            ...(searchQuery && { search: searchQuery }),
        });

        const response = await sendRequest({
            type: `clients/${clientId}/users?${queryParams.toString()}`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to fetch client users");
        }

        return {
            users: response.rows.usersData as IUserRecord[],
            total: response.rows.totalUsers as number,
        };
    } catch (err) {
        console.error("Error fetching client users:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getClientIntegrations = async (clientId: number) => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}/integrations`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(
                response.message || "Failed to fetch client integrations"
            );
        }
        return response.data as IClientIntegration[];
    } catch (err) {
        console.error("Error fetching client integrations:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const updateClientIntegration = async (
    clientId: number,
    channelId: number,
    data: {
        platform_status: boolean;
        default_account_name?: string;
        unq_account_id?: string;
        account_username?: string;
        fk_ba_account_id?: string;
        fk_aa_account_id?: string;
        password?: string;
    }
) => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}/integrations/${channelId}`,
            method: "PUT",
            data,
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to update integration");
        }

        return response.data;
    } catch (err) {
        console.error("Error updating integration:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const updateClientUser = async (
    clientId: number,
    userId: number,
    userData: Partial<IUserRecord>
) => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}/users/${userId}`,
            method: "PUT",
            data: userData,
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to update user");
        }

        return response.data;
    } catch (err) {
        console.error("Error updating user:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getGenerateMediaPlanData = async (planLevels: string[]) => {
    try {
        const response = await sendRequest({
            type: `media-planning/generate`,
            method: "POST",
            data: {
                planLevels,
            },
        });
        if (!response) {
            throw new Error("Unable to get channels data !!");
        }

        return mapObject(response.rows, (level) => decompressData(level));
    } catch (err: any) {
        showToast("danger", err.message);
    }
};

export const getPlanTrackingData = async (
    planTrackingConfig: TPlanTrackingConfig
) => {
    try {
        const response = await sendRequest({
            type: `media-planning/track`,
            method: "POST",
            data: planTrackingConfig,
        });

        if (!response) {
            throw new Error("Unable to get channels data !!");
        }

        return mapObject(response.rows, (level) => decompressData(level));
    } catch (err: any) {
        Sentry.captureException(err);
    }
};

export const getClientJobs = async (
    clientId: number,
    page = 1,
    pageSize = 10,
    searchQuery?: string
) => {
    try {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
            ...(searchQuery && { search: searchQuery }),
        });

        const response = await sendRequest({
            type: `clients/${clientId}/jobs?${queryParams.toString()}`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to fetch client jobs");
        }

        return {
            jobs: response.data.rows as IClientJob[],
            total: response.data.total as number,
            page: response.data.page as number,
            pageSize: response.data.pageSize as number,
            totalPages: response.data.totalPages as number,
        };
    } catch (err) {
        console.error("Error fetching client jobs:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const createClientJob = async (
    clientId: number,
    jobData: Partial<IClientJob>
) => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}/jobs`,
            method: "POST",
            data: jobData,
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to create client job");
        }

        return response.data;
    } catch (err) {
        console.error("Error creating client job:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const updateClientJobStatus = async (
    clientId: number,
    jobId: number,
    status: boolean
) => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}/jobs/${jobId}/status`,
            method: "PUT",
            data: { status },
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to update job status");
        }

        return response.data;
    } catch (err) {
        console.error("Error updating job status:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getChannelReports = async (channelId: number) => {
    try {
        const response = await sendRequest({
            type: `reports/channels/${channelId}/reports`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(
                response.message || "Failed to fetch channel reports"
            );
        }

        return response.data;
    } catch (err) {
        console.error("Error fetching channel reports:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getClientBrands = async (
    clientId: number,
    page = 1,
    pageSize = 10,
    searchQuery?: string
) => {
    try {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
            ...(searchQuery && { search: searchQuery }),
        });

        const response = await sendRequest({
            type: `clients/${clientId}/brands?${queryParams.toString()}`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(
                response.message || "Failed to fetch client brands"
            );
        }

        return {
            brands: response.data.rows,
            total: response.data.total as number,
            page: response.data.page as number,
            pageSize: response.data.pageSize as number,
            totalPages: response.data.totalPages as number,
        };
    } catch (err) {
        console.error("Error fetching client brands:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const createClientBrand = async (
    clientId: number,
    brandData: ICreateBrandPayload
) => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}/brands`,
            method: "POST",
            data: brandData,
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to create brand");
        }

        return response.data;
    } catch (err) {
        console.error("Error creating brand:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const deleteClientBrand = async (clientId: number, brandId: number) => {
    try {
        const response = await sendRequest({
            type: `clients/${clientId}/brands/${brandId}`,
            method: "DELETE",
        });

        if (!response.status) {
            throw new Error(response.message || "Failed to delete brand");
        }

        return response.data;
    } catch (err) {
        console.error("Error deleting brand:", err);
        Sentry.captureException(err);
        throw err;
    }
};

const fetchCategoryModuleData = async (
    date: TDate,
    setModuleData: React.Dispatch<React.SetStateAction<any>>,
    module: string,
    selectedCategory: string[],
    selectedSubCategory: string[],
    selectedBrand: string[],
    caseType: number,
    selectedCatAdditionalFilters: ICategoryAdvFilters,
    section?: string
) => {
    try {
        let formattedData: any;

        const formattedStartDate = date.startDate.format("YYYY-MM-DD");
        const formattedEndDate = date.endDate.format("YYYY-MM-DD");

        const data = await getCategoryInsightsData({
            module,
            section,
            previousStart: formattedStartDate,
            previousEnd: formattedEndDate,
            start: formattedStartDate,
            end: formattedEndDate,
            selectedCategory,
            selectedSubCategory,
            selectedBrand,
            caseType,
            selectedCatAdditionalFilters,
        });
        const channel = store.getState().channel.value;
        const isQcommChannel = channelTypeMapping.qcomm.includes(channel);

        let result;

        if (
            [
                ProductDeepdiveModules.OVERVIEW,
                ProductDeepdiveModules.AVAILABILITY,
            ].includes(module as ProductDeepdiveModules)
        ) {
            result = formatDataBasedOnFrequency(
                data,
                module,
                SubModule.CATEGORY_INSIGHTS
            );
        }

        switch (module) {
            case ProductDeepdiveModules.OVERVIEW:
                formattedData = {
                    dailyData: formatCategoryOverviewData(
                        result.rawData,
                        section,
                        date
                    ),
                    weeklyData: formatCategoryOverviewData(
                        result.weeklyData,
                        section,
                        date
                    ),
                    monthlyData: formatCategoryOverviewData(
                        result.monthlyData,
                        section,
                        date
                    ),
                };
                setModuleData((prev: any = {}) => {
                    const mergeData = (
                        timeframe:
                            | FREQUENCY_SLICER_OPTIONS.DAILY_DATA
                            | FREQUENCY_SLICER_OPTIONS.WEEKLY_DATA
                            | FREQUENCY_SLICER_OPTIONS.MONTHLY_DATA
                    ) => {
                        const prevData = prev?.[timeframe] ?? {};
                        const formattedTimeframeData = formattedData[timeframe];
                        return {
                            ...(section ===
                                CategoryInsightsOverviewSections.SERP &&
                            prevData
                                ? prevData
                                : formattedTimeframeData),
                            ...Object.fromEntries(
                                keysToMergeCategoryOverviewModuleSectionData.map(
                                    (key) => [
                                        key,
                                        section ===
                                        CategoryInsightsOverviewSections.SERP
                                            ? formattedTimeframeData[key]
                                            : prevData?.[key] ??
                                              formattedTimeframeData[key],
                                    ]
                                )
                            ),
                        };
                    };

                    return {
                        dailyData: mergeData(
                            FREQUENCY_SLICER_OPTIONS.DAILY_DATA
                        ),
                        weeklyData: mergeData(
                            FREQUENCY_SLICER_OPTIONS.WEEKLY_DATA
                        ),
                        monthlyData: mergeData(
                            FREQUENCY_SLICER_OPTIONS.MONTHLY_DATA
                        ),
                    };
                });
                break;
            case ProductDeepdiveModules.AVAILABILITY:
                // !For ECOMM
                if (isQcommChannel) {
                    formattedData = {
                        dailyData: formatCategoryAvailabilityData(
                            result?.rawData
                        ),
                        weeklyData: formatCategoryAvailabilityData(
                            result?.weeklyData
                        ),
                        monthlyData: formatCategoryAvailabilityData(
                            result?.monthlyData
                        ),
                    };
                } else {
                    const dailyData = formatCategoryAvailabilityDataEcomm(
                        result?.rawData[0]
                    );
                    const weeklyData = formatCategoryAvailabilityDataEcomm(
                        result?.weeklyData[0]
                    );
                    const monthlyData = formatCategoryAvailabilityDataEcomm(
                        result?.monthlyData[0]
                    );

                    formattedData = {
                        dailyData: dailyData,
                        weeklyData: {
                            ...weeklyData,
                            summaryWidgetData: dailyData.summaryWidgetData,
                            availabilityIssuesByRegionAndSubcategory:
                                dailyData.availabilityIssuesByRegionAndSubcategory,
                            myBrandVsCompetitionData:
                                dailyData.myBrandVsCompetitionData,
                        },
                        monthlyData: {
                            ...monthlyData,
                            summaryWidgetData: dailyData.summaryWidgetData,
                            availabilityIssuesByRegionAndSubcategory:
                                dailyData.availabilityIssuesByRegionAndSubcategory,
                            myBrandVsCompetitionData:
                                dailyData.myBrandVsCompetitionData,
                        },
                    };
                }
                break;
            default:
                console.warn(`Unhandled module: ${module}`);
                break;
        }
        if (module !== ProductDeepdiveModules.OVERVIEW) {
            setModuleData(formattedData);
        }
    } catch (err) {
        console.error("Error loading data:", err);
    }
};

export const fetchCategoryInsightsModulesData = async (
    module: string,
    date: TDate,
    setModuleData: React.Dispatch<React.SetStateAction<any>>,
    setLoadingState: React.Dispatch<TPaModuleReducerActions>,
    selectedCategory: string[],
    selectedSubCategory: string[],
    selectedBrand: string[],
    caseType: number,
    selectedCatAdditionalFilters: ICategoryAdvFilters,
    sections?: string[]
) => {
    try {
        setLoadingState({
            type: module as ProductAndCategoryAnalyticsModuleType,
            payload: true,
        });

        const fetchSection = async (section: string) => {
            try {
                await fetchCategoryModuleData(
                    date,
                    setModuleData,
                    module,
                    selectedCategory,
                    selectedSubCategory,
                    selectedBrand,
                    caseType,
                    selectedCatAdditionalFilters,
                    section
                );
                setLoadingState({
                    type: module as ProductAndCategoryAnalyticsModuleType,
                    payload: false,
                });
            } catch (error) {
                console.error(`Error fetching section ${section}:`, error);
                setLoadingState({
                    type: module as ProductAndCategoryAnalyticsModuleType,
                    payload: false,
                });
            }
        };

        if (sections && sections.length > 0) {
            await Promise.all(sections.map(fetchSection));
        } else {
            await fetchCategoryModuleData(
                date,
                setModuleData,
                module,
                selectedCategory,
                selectedSubCategory,
                selectedBrand,
                caseType,
                selectedCatAdditionalFilters
            );
        }

        setLoadingState({
            type: module as ProductAndCategoryAnalyticsModuleType,
            payload: false,
        });
    } catch (err) {
        setLoadingState({
            type: module as ProductAndCategoryAnalyticsModuleType,
            payload: false,
        });
        console.error("Error loading data:", err);
    }
};

export const fetchCategoryAnalysisFiltersData = async (module: string) => {
    try {
        const section = "filters";
        const params = new URLSearchParams({
            module,
            section,
        });
        const response = await sendRequest({
            type: `analytics/category-insights?${params.toString()}`,
            method: "get",
        });

        if (!response) throw new Error(response.message);

        return response.rows[0] || null;
    } catch (err) {
        console.error("Error loading data:", err);
    }
};

export const updateReportsOrder = async (reports: any) => {
    const response = await sendRequest({
        type: "revstack/order",
        method: "PUT",
        data: reports,
    });
    return response;
};

export const updateReportAccess = async (
    reportId: string,
    accessType: string,
    payload?: any
) => {
    const response = await sendRequest({
        type: `revstack/${reportId}/${accessType}/access`,
        method: "PATCH",
        data: payload,
    });
    return response;
};

export const addReportAccess = async (
    usersToAdd: IAddReportAccessData[],
    reportId: string
) => {
    const response = await sendRequest({
        type: `revstack/${reportId}/access`,
        method: "POST",
        data: { usersToAdd: usersToAdd },
    });
    return response;
};

export const getUsersWithReportAccess = async (
    reportId: string,
    visibility?: string
) => {
    const url = visibility
        ? `revstack/${reportId}/access?visibility=${visibility}`
        : `revstack/${reportId}/access`;

    const response = await sendRequest({
        type: url,
        method: "GET",
    });
    return response;
};

export const removeReportAccess = async (reportId: string, userId: number) => {
    const response = await sendRequest({
        type: `revstack/${reportId}/access/${userId}`,
        method: "DELETE",
    });
    return response;
};

export const clearReportUserRestrictions = async (reportId: string) => {
    const response = await sendRequest({
        type: `revstack/${reportId}/users/clear`,
        method: "DELETE",
    });
    return response;
};

export const updateDefaultClient = async (clientId: number) => {
    try {
        const response = await sendRequest({
            type: "user/default-client",
            method: "PATCH",
            data: {
                client_id: clientId,
            },
        });
        return response;
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
    }
};

export const getRevstackMainFilters = async () => {
    const response = await sendRequest({
        type: "clients/tags/mapping",
        method: "GET",
    });
    return response;
};

export function startEventSourceStream(
    config: IConfigs,
    endpoint: string,
    onMessage?: (data: any) => void
) {
    let url = endpoint;
    const API_DOMAIN = `${
        process.env.VITE_NODE_ENV !== "local"
            ? `${window.location.protocol}//${window.location.host}`
            : ""
    }${process.env.VITE_API_DOMAIN || "/api"}`;

    url = API_DOMAIN + endpoint;

    if (
        config.method?.toLowerCase() === "get" &&
        config.data &&
        typeof config.data === "object"
    ) {
        const params = new URLSearchParams(config.data).toString();
        if (params) url += `?${params}`;
    }
    const es = new window.EventSource(url, { withCredentials: true });
    es.onmessage = (event) => {
        try {
            const parsed = JSON.parse(event.data);
            if (onMessage) onMessage(parsed);
        } catch {
            if (onMessage) onMessage(event.data);
        }
    };

    // Listen for the custom 'end' event to close the connection
    es.addEventListener("end", (event) => {
        if (onMessage) onMessage(event.data);
        es.close();
    });

    es.onerror = (err) => {
        console.error("[SSE] Error:", err);
    };
    return es; // Call es.close() to stop
}

export const getRevstackChannels = async () => {
    try {
        const response = await sendRequest({
            type: "revstack/channels",
            method: "GET",
        });

        if (!response.status) {
            throw new Error(
                response.message || "Failed to fetch Revstack channels"
            );
        }

        return response.rows;
    } catch (err) {
        console.error("Error fetching Revstack channels:", err);
        Sentry.captureException(err);
        throw err;
    }
};

export const getRevstackCardTableData = async (
    sectionData: any,
    cardType: string,
    cardId: string,
    relatedComponentDataKeys: string[]
) => {
    try {
        store.dispatch(
            removeRevstackDashboardWidgetDataKey(relatedComponentDataKeys)
        );

        const data = {
            cardId,
            cardType,
        };

        const widgetConfig = filter(
            sectionData.config.widgets,
            (widget) =>
                widget.identifier ===
                COMPONENT_FILTER_QUERY_CONFIG[cardType].sectionWidgetKey
        );

        // Add cubeId to widgets if not present (for backward compatibility)
        const processedWidgetConfig = widgetConfig.map((widget) => {
            if (!widget.cubeId) {
                widget.cubeId = sectionData.config.sectionId;
            }
            return widget;
        });

        //! Review the cubeId logic
        const cubeId =
            processedWidgetConfig[0]?.cubeId || sectionData.config.sectionId;
        const { cubeFilters, cubeDateObject } = generateCubeFilters(
            cubeId,
            COMPONENT_FILTER_QUERY_CONFIG[cardType].filterKey,
            cardId
        );

        const cubeQueries = buildQueriesFromWidgetsByCubeId(
            [processedWidgetConfig],
            cubeDateObject,
            cubeFilters
        );

        const response = await sendRequest({
            type: `category-analysis/${cardType}`,
            method: "POST",
            data: {
                ...data,
                queryPayloads: cubeQueries,
            },
        });

        if (!response.status) {
            throw new Error(`Failed to get ${cardType} data!`);
        }

        store.dispatch(updateRevstackDashboardWidgetData(response.data));
        return response.data;
    } catch (err) {
        console.error(err);
        return [];
    }
};

export const getRevstackData = async (options: {
    cubeQueries: any[];
    onStreamData?: (data: any) => void;
}): Promise<any> => {
    const { cubeQueries, onStreamData } = options;

    try {
        if (!cubeQueries || cubeQueries.length === 0) {
            showToast("warning", "No data queries provided for trends");
            return null;
        }

        const response = await sendRequest(
            {
                type: "category-analysis",
                method: "POST",
                data: cubeQueries,
            },
            true,
            false,
            true,
            "application/json",
            {
                isStreaming: true,
                onMessage: (parsedStreamData: any) => {
                    if (onStreamData) {
                        onStreamData(parsedStreamData);
                    }
                },
            }
        );

        return response;
    } catch (error: any) {
        console.error("Error fetching trends data:", error);
        showToast("danger", error?.message || "Failed to update trends data");
        return null;
    }
};

export const getRevstackAiInsights = async (payload: any) => {
    try {
        const response = await sendRequest({
            type: `category-analysis/insights`,
            method: "POST",
            data: payload,
        });

        if (!response.status) {
            throw new Error(`Failed to get insights data!`);
        }

        return response.data;
    } catch (err) {
        console.error(err);
        return [];
    }
};

export const getKeywordCompetitionSOVData = async (payload: any) => {
    try {
        const response = await sendRequest({
            type: `category-analysis/keyword/sov`,
            method: "POST",
            data: payload,
        });

        if (!response.status) {
            throw new Error(`Failed to get insights data!`);
        }

        return response.data;
    } catch (err) {
        console.error(err);
        return [];
    }
};

export const getCompetitionSOVData = async (keyword: string) => {
    try {
        const { cubeDateObject, cubeFilters } = generateCubeFilters(
            RevstackCubeIds.KA_NATIONAL
        );

        // Extract selected brand ids from cubeFilters (brand_filter)
        const selectedBrandIds: number[] =
            cubeFilters.find((f: any) => f.member.includes("brand_filter"))
                ?.values || [];
        const kwSovFilters = [
            ...cubeFilters,
            {
                values: [keyword],
                member: `${RevstackCubeIds.KA_NATIONAL}.keyword`,
                operator: "equals",
            },
        ];

        const widgetRows = [
            {
                identifier: "keyword_competition_sov",
                type: WidgetType.W8,
                cubeId: RevstackCubeIds.KA_NATIONAL,
                measures: [
                    CommonWidgetMetrics.GENERALISED_ORGANIC_SOV,
                    CommonWidgetMetrics.GENERALISED_PAID_SOV,
                    CommonWidgetMetrics.GENERALISED_OVERALL_SOV,
                ],
                queryDimensions: ["sku_brand", "sku_brand_id"],
                queryMetaInfo: {
                    filterToSkip: "brand_filter",
                },
            },
        ];

        const cubeQueries = buildQueriesFromWidgetsByCubeId(
            [widgetRows], // Wrap in array to match expected structure
            cubeDateObject,
            kwSovFilters
        );
        const queryPayload = cubeQueries[0];

        const response = await sendRequest({
            type: `category-analysis/keyword/sov`,
            method: "POST",
            data: {
                queryPayload,
            },
        });

        if (!response.status) {
            throw new Error(`Failed to get competition SOV data!`);
        }

        const compSovData = (response.data?.length ? response.data : []).filter(
            (r: any) =>
                !selectedBrandIds?.some(
                    (id: any) => Number(id) === Number(r.sku_brand_id)
                )
        );

        return compSovData.length
            ? compSovData.map((r: any) => ({
                  name: r.sku_brand,
                  overallSov: r.generalised_overall_sov_value,
                  overallChange: r.generalised_overall_sov_change,
                  organicSov: r.generalised_organic_sov_value,
                  organicChange: r.generalised_organic_sov_change,
                  paidSov: r.generalised_paid_sov_value,
                  paidChange: r.generalised_paid_sov_change,
              }))
            : [];
    } catch (err: any) {
        console.error(err);
        return [];
    }
};

export const getRevstackPopoverData = async (
    filterValue: string | number,
    widgetConfigs?: any[],
    skipPopOverFilter = false
) => {
    try {
        if (!widgetConfigs || widgetConfigs.length === 0) {
            return [];
        }
        const allCubeQueries: any[] = [];

        for (const config of widgetConfigs) {
            const cubeId = config.cubeId;

            const { cubeDateObject, cubeFilters } = generateCubeFilters(cubeId);

            const popoverFilter = config.queryMetaInfo?.popoverFilter;

            const filtersToIgnore: string[] =
                config.queryMetaInfo?.filtersToIgnore || [];

            const filteredCubeFilters = cubeFilters.filter((filter: any) => {
                // Only identify filter to replace if skipPopOverFilter is false
                const isFilterToReplace =
                    !skipPopOverFilter &&
                    popoverFilter &&
                    (filter.member === `${cubeId}.${popoverFilter}` ||
                        filter.member === popoverFilter);

                const shouldRemove =
                    filtersToIgnore.length > 0 &&
                    filtersToIgnore.some(
                        (filterKey) =>
                            filter.member === filterKey ||
                            filter.member === `${cubeId}.${filterKey}`
                    );

                // Keep the filter if it's NOT to be replaced AND NOT to be removed
                // If skipPopOverFilter is true, isFilterToReplace will be false, so the filter stays
                return !isFilterToReplace && !shouldRemove;
            });

            const widgetRows = [config];

            // Add the replacement filter with the filterValue (only if filterToReplace is specified)
            const isChannelPopoverFilter =
                popoverFilter === CommonWidgetMetrics.CHANNEL_ID;
            let normalizedFilterValue: string | number = filterValue;
            const numericValue = Number(filterValue);
            const isStringValue = typeof filterValue === "string";

            if (isChannelPopoverFilter && isStringValue) {
                const formattedChannelKey = filterValue
                    .replace(/\s+/g, "_")
                    .toUpperCase();
                const channelId =
                    Channels[formattedChannelKey as keyof typeof Channels];
                if (channelId) {
                    normalizedFilterValue = channelId;
                }
            } else if (
                (!isStringValue || filterValue.trim() !== "") &&
                !Number.isNaN(numericValue)
            ) {
                normalizedFilterValue = numericValue;
            }

            const filters =
                !skipPopOverFilter && popoverFilter
                    ? [
                          ...filteredCubeFilters,
                          {
                              values: [normalizedFilterValue],
                              member: `${cubeId}.${popoverFilter}`,
                              operator: "equals",
                          },
                      ]
                    : filteredCubeFilters;

            const cubeQueries = buildQueriesFromWidgetsByCubeId(
                [widgetRows],
                cubeDateObject,
                filters
            );

            allCubeQueries.push(...cubeQueries);
        }

        const response = await sendRequest({
            type: `category-analysis/popover-data`,
            method: "POST",
            data: {
                queryPayload: allCubeQueries,
            },
        });

        return response.data;
    } catch (err: any) {
        console.error(err);
        return [];
    }
};

export const getFiltersFromCube = async (
    cubeId: RevstackCubeIds,
    queryId: string,
    queryDimensions: string[],
    isExcludeDimension = false,
    excludeFilter: string[],
    filterToSkip = "",
    applyRegionalFilter = false,
    dimValueToTruncate = ""
) => {
    try {
        const { cubeDateObject, cubeFilters } = generateCubeFilters(
            cubeId,
            "",
            null,
            true
        );

        const widgetRows = [
            {
                identifier: queryId,
                type: WidgetType.W8,
                cubeId: cubeId, // Add cubeId to widget
                measures: [CommonWidgetMetrics.ORGANIC_SOV],
                queryDimensions,
                queryMetaInfo: {
                    ...(filterToSkip ? { filterToSkip } : {}),
                    ...(dimValueToTruncate ? { dimValueToTruncate } : {}),
                },
                ...(isExcludeDimension ? { isExcludeDimension } : {}),
                ...(excludeFilter.length > 0 ? { excludeFilter } : {}),
            },
        ];

        const cubeQueries = buildQueriesFromWidgetsByCubeId(
            [widgetRows],
            cubeDateObject,
            cubeFilters
        );
        const queryPayload = cubeQueries[0];

        const response = await sendRequest({
            type: `category-analysis/trends/filters`,
            method: "POST",
            data: {
                queryPayload,
            },
        });

        if (!response.status) {
            throw new Error(`Failed to get trends filters data!`);
        }

        return response.data;
    } catch (err: any) {
        console.error(err);
        return [];
    }
};

export const getRevstackWidgetData = async (
    type: string,
    sectionData: any,
    componentType: string,
    cateogryIds: (number | string)[],
    relatedComponentDataKeys: string[]
) => {
    try {
        store.dispatch(
            removeRevstackDashboardWidgetDataKey(relatedComponentDataKeys)
        );
        const widgetConfig = filter(
            sectionData.widgets,
            (widget) =>
                widget.identifier ===
                COMPONENT_FILTER_QUERY_CONFIG[componentType].sectionWidgetKey
        );

        // Add cubeId to widgets if not present (for backward compatibility)
        const processedWidgetConfig = widgetConfig.map((widget) => {
            if (!widget.cubeId) {
                widget.cubeId = sectionData.cubeId;
            }
            return widget;
        });

        //! Review the cubeId logic
        const cubeId = processedWidgetConfig[0]?.cubeId || sectionData.cubeId;
        const { cubeFilters } = generateCubeFilters(
            cubeId,
            COMPONENT_FILTER_QUERY_CONFIG[componentType].filterKey,
            cateogryIds
        );

        const cubeQueries = buildQueriesFromWidgetsByCubeId(
            [processedWidgetConfig],
            undefined,
            cubeFilters
        );

        const response = await sendRequest({
            type: type,
            method: "POST",
            data: {
                queryPayloads: cubeQueries,
            },
        });

        if (!response.status) {
            throw new Error(`Failed to get category market analysis data!`);
        }

        store.dispatch(updateRevstackDashboardWidgetData(response.data));

        return response.data;
    } catch (err) {
        console.error(err);
        return [];
    }
};

export const getUserActivityLogs = async (
    filters: {
        targetUserId?: string;
        actorUserId?: string;
        activityTypes?: string[];
        startDate?: string;
        endDate?: string;
        search?: string;
        actorName?: string;
    } = {},
    page = 1,
    pageSize = 50
) => {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));

    if (filters.targetUserId)
        params.append("targetUserId", filters.targetUserId);
    if (filters.actorUserId) params.append("actorUserId", filters.actorUserId);
    if (filters.activityTypes?.length)
        params.append("activityTypes", filters.activityTypes.join(","));
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.search) params.append("search", filters.search);
    if (filters.actorName) params.append("actorName", filters.actorName);

    try {
        const response = await sendRequest({
            type: `user/${filters.targetUserId}/logs`,
            method: "GET",
            data: {
                page,
                pageSize,
                actorUserId: filters.actorUserId,
                activityTypes: filters.activityTypes?.join(","),
                startDate: filters.startDate,
                endDate: filters.endDate,
                search: filters.search,
                actorName: filters.actorName,
            },
        });
        return response?.status
            ? response.data
            : { logs: [], pagination: { page, pageSize, total: 0 } };
    } catch (err) {
        showToast("danger", "Failed to fetch activity logs");
        return { logs: [], pagination: { page, pageSize, total: 0 } };
    }
};

export const getActivityLogActors = async () => {
    try {
        const response = await sendRequest({
            type: "user/logs/actors",
            method: "GET",
        });
        if (response?.status && response?.data) {
            return response.data;
        }
        return [];
    } catch (err) {
        return [];
    }
};

export const getProductGroups = async () => {
    try {
        const response = await sendRequest({
            type: `products/groups`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(`Failed to get product groups data!`);
        }

        return response.rows;
    } catch (err) {
        console.error(err);
        return [];
    }
};

export const createClusterGroups = async (payload: any) => {
    try {
        const response = await sendRequest({
            type: `products/clusters`,
            method: "POST",
            data: payload,
        });

        if (!response.status) {
            throw new Error(`Failed to create cluster!`);
        }

        showToast("success", "Cluster created successfully");
        return response;
    } catch (err) {
        console.error(err);
        showToast("danger", "Failed to create cluster");
        return null;
    }
};

export const getClusterGroups = async () => {
    try {
        const response = await sendRequest({
            type: `products/clusters`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(`Failed to get cluster groups!`);
        }

        return response.rows;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const getCitiesList = async () => {
    try {
        const response = await sendRequest({
            type: `/channels/cities`,
            method: "GET",
        });

        if (!response.status) {
            throw new Error(`Failed to get cities list!`);
        }
        return response.rows;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const formatClusterDataWithProducts = (
    clusterData: any[],
    productGroups: any[]
) => {
    const channel = store.getState().channel.value;
    const channelDomain = store.getState().channelDomain.value;
    // Create a map of product groups by sku_group_id for quick lookup
    const productGroupsMap = new Map();
    if (productGroups && Array.isArray(productGroups)) {
        productGroups.forEach((productGroup: any) => {
            productGroupsMap.set(productGroup.sku_group_id, productGroup);
        });
    }

    // Transform the database response to match frontend expected structure
    return clusterData.map((cluster: any) => {
        // Transform cluster properties
        const transformedCluster: any = {
            id: cluster.cluster_id.toString(),
            name: cluster.cluster_name,
            createdAt: cluster.created_at,
            products: [] as any[],
            collections: [] as any[],
        };

        // Transform sku_groups to products array
        if (cluster.sku_groups && Array.isArray(cluster.sku_groups)) {
            cluster.sku_groups.forEach((skuGroup: any) => {
                // Get the complete product data from product groups
                const productGroupData = productGroupsMap.get(
                    skuGroup.sku_group_id
                );

                // Each sku_group represents one product
                const product: any = {
                    id: skuGroup.sku_group_id.toString(), // Use sku_group_id as product ID
                    sku:
                        skuGroup.skuList && skuGroup.skuList.length > 0
                            ? skuGroup.skuList[0].sku
                            : "", // Use first SKU as primary
                    sku_title:
                        skuGroup.skuList && skuGroup.skuList.length > 0
                            ? skuGroup.skuList[0].sku
                            : "", // Use first SKU as title
                    priority:
                        skuGroup.sku_priority !== null &&
                        skuGroup.sku_priority !== undefined
                            ? skuGroup.sku_priority
                            : 0,
                    disabled: false,
                    sku_group_id: skuGroup.sku_group_id,
                    cluster_sku_group_id: skuGroup.cluster_sku_group_id,
                    skuList: skuGroup.skuList || [], // Keep all SKU variants as skuList
                    skuDetails: skuGroup.skuList
                        ? skuGroup.skuList.map((skuItem: any) => ({
                              sku: skuItem.sku,
                              unit: skuItem.unit,
                              sku_group_variant_id:
                                  skuItem.sku_group_variant_id,
                              isActive: skuItem.is_active || false,
                              isActiveForSession: skuItem.is_active || false,
                          }))
                        : [],
                };

                // Enrich with complete product data if available
                if (productGroupData) {
                    const firstSku = productGroupData.sku_details?.[0] || {};

                    // Add all the fields that are used in the UI
                    product.title =
                        productGroupData.sku_group_title ||
                        firstSku.title ||
                        product.sku_title;
                    product.sku_title =
                        productGroupData.sku_group_title ||
                        firstSku.title ||
                        product.sku_title;
                    product.category = productGroupData.category || "-";
                    product.subcategory = productGroupData.subcategory || "-";
                    product.msPercent = productGroupData.market_share_pct || 0;
                    product.sovPercent = productGroupData.overall_sov || 0;
                    product.wtAvailabilityPercent =
                        productGroupData.weighted_availability || 0;
                    product.image = productGroupData.sku_group_thumbnail || "";
                    product.thumbnail =
                        productGroupData.sku_group_thumbnail || "";
                    product.product_url = getProductPageLink(
                        channel as number,
                        product.skuDetails[0]?.sku,
                        channelDomain as string,
                        product.sku_title
                    );

                    // Table column data (with _value and _change suffixes)
                    product.market_share_value =
                        productGroupData.market_share_pct || 0;
                    product.market_share_change =
                        productGroupData.market_share_pct_change || 0;
                    product.weighted_availability_value =
                        productGroupData.weighted_availability || 0;
                    product.weighted_availability_change =
                        productGroupData.weighted_availability_change || 0;
                    product.overall_sov_value =
                        productGroupData.overall_sov || 0;
                    product.overall_sov_change =
                        productGroupData.overall_sov_change || 0;
                    product.organic_sov_value =
                        productGroupData.organic_sov || 0;
                    product.organic_sov_change =
                        productGroupData.organic_sov_change || 0;
                    product.paid_sov_value = productGroupData.paid_sov || 0;
                    product.paid_sov_change =
                        productGroupData.paid_sov_change || 0;
                    product.priority_value =
                        skuGroup.sku_priority !== null &&
                        skuGroup.sku_priority !== undefined
                            ? skuGroup.sku_priority
                            : 0;
                    product.variants_value =
                        productGroupData.sku_details?.length > 1
                            ? productGroupData.sku_details
                                  .map((sku: any) => sku.unit || sku.sku)
                                  .filter(Boolean)
                            : [];

                    // Update skuDetails with readable labels from product group data
                    if (product.skuDetails && productGroupData.sku_details) {
                        product.skuDetails = product.skuDetails.map(
                            (clusterSku: any) => {
                                const productSku =
                                    productGroupData.sku_details.find(
                                        (pgSku: any) =>
                                            pgSku.sku === clusterSku.sku
                                    );
                                return {
                                    ...clusterSku,
                                    unit:
                                        productSku?.unit ||
                                        clusterSku.unit ||
                                        clusterSku.sku,
                                };
                            }
                        );
                    }

                    // Additional fields
                    product.unit = firstSku.unit || "";
                    product.organicSov = productGroupData.organic_sov || 0;
                    product.sponsoredSov = productGroupData.sponsored_sov || 0;
                    product.avgRating = productGroupData.avg_rating || 0;
                    product.isComboGroup =
                        productGroupData.is_combo_group || false;
                    product.isVariantGroup =
                        productGroupData.is_variant_group || false;
                    product.expanded = false;
                }

                transformedCluster.products.push(product);
            });
        }

        return transformedCluster;
    });
};

export const listClusterTargets = async (payload: any) => {
    try {
        const response = await sendRequest({
            type: `products/clusters/targets`,
            method: "POST",
            data: payload,
        });

        if (!response.status) {
            throw new Error(`Failed to get cluster keyword list!`);
        }

        return response;
    } catch (err) {
        console.error(err);
        showToast("danger", "Failed to get cluster keyword list");
        return null;
    }
};

export const getSkuChannelMapping = async (
    sku: string,
    channelId: number
): Promise<any> => {
    try {
        // Fetch specific sku-channel mapping
        const response = await sendRequest({
            type: `category-analysis/sku-channel-mapping`,
            method: "POST",
            data: {
                sku,
                channelId,
            },
        });

        return response.data;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export async function getFiltersByHash(
    hash: string
) {
    try {
        const response = await sendRequest({
            type: `revstack/filters/${hash}`,
            method: 'GET',
        });

        if (response?.status && response?.filters) {
            return response.filters;
        } else {
            showToast('warning', 'Filter configuration not found or has expired');
            return null;
        }
    } catch (error:any) {
        console.error(error.message);
        Sentry.captureException(error.message);
        showToast('danger', error.message);
        return null;
    }
}

export const getAdvancedFilters = async (
    params:IAdvancedFiltersParams
): Promise<any> => {
    
    try {
        const response = await sendRequest({
            type: "revstack/features/list",
            method: "POST",
            data: params
        },
        true,
        false
    );

        return response.data;
    } catch (err) {
        console.error(err);
        return null;
    }
}

export const getBCCKeywordsCSVTemplate = async () => {
    try {
        // Set isExcelUpload to true so sendRequest returns raw Response object
        store.dispatch(updateIsExcelUpload(true));
        
        const response = await sendRequest({
            type: `products/keywords/template`,
            method: "POST",
        });
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "bcc_keywords_template.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return true;
    } catch (err) {
        console.error(err);
        showToast("danger", "Failed to get BCC keywords CSV template");
        return null;
    } finally {
        // Reset isExcelUpload flag
        store.dispatch(updateIsExcelUpload(false));
    }
};

export const triggerMappingRefresh = async () => {
    try {
        const response = await sendRequest({
            type: "products/mapping/refresh",
            method: "GET",
        });

        if (!response.status) {
            throw new Error(response.message);
        }

        return response.data;
    } catch (error: any) {
        Sentry.captureException(error);
        return null;
    }
}

export const getMappingRefreshStatus = async () => {
    try {
        const response = await sendRequest({
            type: "products/mapping/status",
            method: "GET",
        });

        if (!response.status) {
            throw new Error(response.message);
        }

        return response.data;
    } catch (error: any) {
        Sentry.captureException(error);
        return [];
    }
}